import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import { X, type LucideIcon } from "lucide-react";
import { cn } from "@hey404/ui";

/**
 * The one dialog shell for the platform and the admin: a light veil, a cream
 * card with the chip border and small hard shadow, a tilted icon tile in the
 * action's tone, a card-sized title with a short description, the body, and
 * an optional footer strip for the actions. Escape and the veil close it;
 * focus moves into the dialog and returns to where it was.
 *
 * Kept free of admin-ui imports so admin-ui (CreateModal) can build on it.
 */

// ── Scroll lock ─────────────────────────────────────────────────────────────
// The page scrolls on <html> (styles.css keeps overflow-y: scroll there for a
// stable gutter), so that is the element to lock while a modal is open. Only
// <html>: setting overflow on <body> too turns it into a scroll container and
// breaks the sticky sidebars, which then jump or shrink behind the modal. A
// counter keeps stacked dialogs (a confirm over a form) from unlocking early;
// scrollbar-gutter: stable keeps the layout from shifting.
let activeLocks = 0;
let savedOverflow: string | null = null;

export function useScrollLock(active = true) {
  useEffect(() => {
    if (!active) return;
    const root = document.documentElement;
    if (activeLocks++ === 0) {
      savedOverflow = root.style.overflow;
      root.style.overflow = "hidden";
    }
    return () => {
      if (--activeLocks === 0 && savedOverflow !== null) {
        root.style.overflow = savedOverflow;
        savedOverflow = null;
      }
    };
  }, [active]);
}

/** Locks page scroll while mounted; drop it inside any custom overlay. */
export function ScrollLock() {
  useScrollLock(true);
  return null;
}

const tiles = {
  lemon: "bg-lemon",
  acid: "bg-acid",
  bubble: "bg-bubble",
  grape: "bg-grape",
  ink: "bg-ink text-paper",
} as const;

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  kicker?: string;
  icon?: LucideIcon;
  tone?: keyof typeof tiles;
  footer?: ReactNode;
  size?: "md" | "lg";
  children?: ReactNode;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  kicker,
  icon: Icon,
  tone = "lemon",
  footer,
  size = "md",
  children,
}: DialogProps) {
  const titleId = useId();
  const descriptionId = useId();
  const panelRef = useRef<HTMLElement>(null);
  useScrollLock(open);
  useEffect(() => {
    if (!open) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    // Focus the panel unless a field inside asked for autofocus.
    requestAnimationFrame(() => {
      if (!panelRef.current?.contains(document.activeElement))
        panelRef.current?.focus();
    });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      previousFocus?.focus?.();
    };
  }, [open, onClose]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 grid place-items-center p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.16 }}
        >
          <button
            type="button"
            tabIndex={-1}
            aria-label="Cerrar"
            onClick={onClose}
            className="bg-ink/35 absolute inset-0 cursor-default touch-none backdrop-blur-[2px]"
          />
          <motion.section
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descriptionId : undefined}
            tabIndex={-1}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.22, 0.8, 0.25, 1] }}
            className={cn(
              "border-ink bg-paper border-chip shadow-brutal relative flex max-h-[calc(100dvh-2rem)] w-full flex-col outline-none",
              size === "lg" ? "max-w-xl" : "max-w-md",
            )}
          >
            <header className="flex items-start gap-4 px-6 pb-4 pt-6">
              {Icon && (
                <span
                  aria-hidden="true"
                  className={cn(
                    "border-ink border-chip grid h-11 w-11 shrink-0 rotate-[-4deg] place-items-center",
                    tiles[tone],
                  )}
                >
                  <Icon size={20} />
                </span>
              )}
              <div className="min-w-0 flex-1">
                {kicker && (
                  <p className="text-ink-soft font-mono text-xs font-bold uppercase tracking-[0.06em]">
                    {kicker}
                  </p>
                )}
                <h2
                  id={titleId}
                  className="font-display text-2xl font-extrabold leading-tight"
                >
                  {title}
                </h2>
                {description && (
                  <div
                    id={descriptionId}
                    className="text-ink-soft mt-1.5 text-sm leading-relaxed"
                  >
                    {description}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Cerrar"
                className="text-ink-soft hover:bg-paper-dim hover:text-ink rounded-control -mr-2 -mt-2 grid h-10 w-10 shrink-0 cursor-pointer place-items-center transition-colors"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </header>
            {children && (
              <div className="dialog-body min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-6 text-sm leading-relaxed">
                {children}
              </div>
            )}
            {footer && (
              <footer className="border-ink/20 bg-paper-dim/60 flex flex-wrap justify-end gap-3 border-t border-dashed px-6 py-4">
                {footer}
              </footer>
            )}
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
